/**
 * FinanceIntelligenceDashboardCard.js
 *
 * Compact "Finance checks" card for the management dashboard: VAT issue count
 * of the last 30 days, the number of cashiers at the `review` level (ONLY when
 * the cashier call succeeds: it answers 403 for non-managers) and the top
 * supplier saving potential. The card hides itself when the VAT call fails or
 * the user has no access, so an optional AI surface never shows an error.
 *
 * @module components/management/FinanceIntelligenceDashboardCard
 */
import React, {useCallback, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {Card} from './ManagementUI';
import Icon from './SafeIcon';
import {getManagementScope} from '../../helpers/managementApi';
import {dashboardCardKey, readDashboardCard, writeDashboardCard} from '../../helpers/dashboardCardCache';
import {getCashierAnomalies, getSupplierPrices, getVatCheck} from '../../helpers/financeIntelligenceApi';
import {countReviewFlags, formatMoney, topSupplierSaving, windowToRange} from '../../helpers/financeIntelligence';
import {useManagementAccess} from '../../helpers/managementAccess';
import {useManagementTheme} from '../../helpers/managementTheme';
import useAiAssistantGate from '../../screens/assistant/components/useAiAssistantGate';

/**
 * @returns {?JSX.Element} the card, or `null` when not accessible / not loaded / failed.
 */
export default function FinanceIntelligenceDashboardCard() {
    const {t} = useTranslation();
    const navigation = useNavigation();
    const m = useManagementTheme();
    const {canAccess} = useManagementAccess();
    const aiEnabled = useAiAssistantGate();
    const allowed = aiEnabled && canAccess('financeIntelligence').allowed;

    const [summary, setSummary] = useState(null);

    useFocusEffect(useCallback(() => {
        if (!allowed) {
            setSummary(null);

            return undefined;
        }
        let active = true;

        (async () => {
            const scope = await getManagementScope();
            // Fresh summary from a previous focus/mount (2 min TTL): skip the three invoice-wide scans.
            const cacheKey = dashboardCardKey('finint', scope);
            const cached = readDashboardCard(cacheKey);

            if (cached) {
                if (active) setSummary(cached);

                return;
            }

            const {from, to} = windowToRange(30);
            // Independent calls, each already non-throwing ({ok}); Promise.all is Hermes-safe.
            const [vat, cashier, suppliers] = await Promise.all([
                getVatCheck({from, to, direction: 'all', limit: 1}, scope),
                getCashierAnomalies({days: 30, limit: 25}, scope),
                getSupplierPrices({days: 90, minSavingPct: 3, limit: 1}, scope),
            ]);

            if (!active) return;
            if (!vat.ok || !Number.isFinite(Number(vat.data?.issuesCount))) {
                setSummary(null);

                return;
            }
            const nextSummary = {
                vatIssues: Number(vat.data.issuesCount),
                reviewFlags: cashier.ok ? countReviewFlags(cashier.data) : null,
                saving: suppliers.ok ? topSupplierSaving(suppliers.data) : null,
            };

            setSummary(nextSummary);
            writeDashboardCard(cacheKey, nextSummary);
        })();

        return () => { active = false; };
    }, [allowed]));

    if (!allowed || !summary) return null;

    return (
        <TouchableOpacity activeOpacity={0.85} onPress={() => navigation.navigate('FinanceIntelligenceScreen')}>
            <Card style={styles.card}>
                <View style={styles.head}>
                    <Icon name="shield-check-outline" size={22} color="#F97316" />
                    <Text style={[styles.title, {color: m.colors.heading}]} numberOfLines={1}>{t('finint.dash_title')}</Text>
                    <Icon name="chevron-right" size={20} color={m.colors.subText} />
                </View>
                <Text style={[styles.line, {color: summary.vatIssues > 0 ? '#F97316' : m.colors.subText}]}>
                    {summary.vatIssues > 0 ? t('finint.dash_vat_issues', {count: summary.vatIssues}) : t('finint.dash_vat_clean')}
                </Text>
                {summary.reviewFlags !== null && summary.reviewFlags > 0 && (
                    <Text style={[styles.line, {color: m.colors.subText}]}>{t('finint.dash_review_flags', {count: summary.reviewFlags})}</Text>
                )}
                {!!summary.saving && (
                    <Text style={[styles.line, {color: m.colors.subText}]}>{t('finint.dash_saving', {value: formatMoney(summary.saving.amount, summary.saving.currency)})}</Text>
                )}
            </Card>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    card: {marginBottom: 12},
    head: {flexDirection: 'row', alignItems: 'center', gap: 10},
    title: {flex: 1, fontSize: 15, fontWeight: '700'},
    line: {fontSize: 13.5, fontWeight: '600', marginTop: 6},
});
